"use client";

import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLayout from "../../../navigation/Layout/Layout";
import ProvidersList from "../../components/ProvidersList/ProvidersList";
import AppointmentTabs from "../../components/AppointmentTabs/AppointmentTabs";

import { useTour } from "../../../appointments/hooks/useTour";
import { appointmentsTourSteps } from "./appointmentsTourConfig";

import { fetchDoctors } from "../../../authDoctor/redux/thunks/doctorsThunks";
import {
  selectDoctors,
  selectDoctorLoading,
  selectDoctorError,
} from "../../../authDoctor/redux/selectors/doctorSelectors";

import "./Appointments.css";

export default function Appointments() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const highlightId = searchParams.get("highlight");

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  const doctors = useSelector(selectDoctors);
  const loading = useSelector(selectDoctorLoading);
  const error = useSelector(selectDoctorError);

  useEffect(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  const retryLoadDoctors = useCallback(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "appointments";

    if (hasGuide || hasTour) {

      setTimeout(() => {
        const appointmentsMain = document.querySelector(".appointments-main");
        const providersSection = document.querySelector(".providers-section");
        const appointmentsSections = document.querySelector(
          ".appointments-sections"
        );

        if (appointmentsMain && providersSection && appointmentsSections) {
          setShouldStartTour(true);
        } else {
          console.warn("⚠️ Elements not ready, retrying...");
          setTimeout(() => setShouldStartTour(true), 1500);
        }
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, appointmentsTourSteps);

  if (error && !loading) {
    return (
      <AppLayout>
        <main className={`appointments-main ${isRTL ? "is-rtl" : ""}`}>
          <div className="appointments-error-container">
            <div className="appointments-error-content">
              <div className="error-icon">⚠️</div>

              <h2 className="error-title">
                {t(
                  "appointments.errorTitle",
                  "Impossible de charger les rendez-vous"
                )}
              </h2>

              <p className="error-message">
                {t(
                  "appointments.errorLoadingDoctors",
                  "Erreur lors du chargement"
                )}{" "}
                {String(error || "")}
              </p>

              <p className="error-suggestion">
                {t(
                  "appointments.errorSuggestion",
                  "Veuillez essayer de rafraîchir la page ou de réessayer."
                )}
              </p>

              <div className="error-actions">
                <button className="btn-primary" onClick={retryLoadDoctors}>
                  🔄 {t("appointments.actions.retry", "Réessayer")}
                </button>

                <button className="btn-secondary" onClick={refreshPage}>
                  🔃 {t("appointments.actions.refresh", "Rafraîchir")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <main className={`appointments-main ${isRTL ? "is-rtl" : ""}`}>
        <div className="appointments-row">
          <section className="appointments-section providers-section">
            {loading && (
              <div className="dd-loading">
                {t("appointments.loadingDoctors")}
              </div>
            )}

            {!loading && <ProvidersList doctors={doctors} />}
          </section>

          <section className="appointments-sections">
            {/* ✅ IMPORTANT : passer doctors ici */}
            <AppointmentTabs
              doctors={doctors}
              initialTab={searchParams.get("tab")}
              highlightId={highlightId}
            />
          </section>
        </div>
      </main>
    </AppLayout>
  );
}